import { useTranslation } from 'react-i18next'
import { Button, type SubscriptionStatus } from '@structural-climbing/ui'
import { SubBadge } from './SubBadge'

/** A subscription as the member and child profile endpoints return it. */
export interface ProfileSubscriptionData {
  id: string
  planName: string
  planType: string
  status: SubscriptionStatus
  startsAt: string | null
  expiresAt: string | null
  entriesRemaining?: number | null
  /** The current plan's freeze window, if it has one. */
  frozenFrom?: string | null
  frozenUntil?: string | null
  /** History rows only: this was an add-on rather than a main plan. */
  isAddon?: boolean
  /** Current and upcoming plans: the plan sold, so a renewal can offer the same one. */
  gymPlanId?: string
  /** Current plan only: the gym's renewal discount, while it applies. */
  renewalOffer?: { percent: number } | null
  /** Upcoming plan only: what was paid, and the renewal discount if it was given. */
  pricePaid?: number
  renewalDiscountPercent?: number | null
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function SubscriptionDates({ sub }: { sub: ProfileSubscriptionData }) {
  const { t } = useTranslation()
  if (!sub.startsAt && !sub.expiresAt) return null
  return (
    <p className="mt-0.5 text-xs text-foreground/50">
      {sub.startsAt && t('members.subStart', { date: formatDate(sub.startsAt) })}
      {sub.startsAt && sub.expiresAt && ' · '}
      {sub.expiresAt && t('members.subEnd', { date: formatDate(sub.expiresAt) })}
    </p>
  )
}

/** A freeze that is under way or still ahead. One that is over is just history. */
export function hasOpenFreeze(sub: ProfileSubscriptionData, now = new Date()): boolean {
  return !!sub.frozenFrom && !!sub.frozenUntil && new Date(sub.frozenUntil) >= now
}

/** Day passes and add-ons cannot be frozen, and neither can a plan with no end. */
function canFreeze(sub: ProfileSubscriptionData): boolean {
  return (
    (sub.status === 'active' || sub.status === 'scheduled') &&
    sub.planType !== 'day_pass' &&
    !!sub.expiresAt &&
    !hasOpenFreeze(sub)
  )
}

export function CurrentSubscriptionCard({
  sub,
  onFreeze,
  onUnfreeze,
}: {
  sub: ProfileSubscriptionData
  /** Given only to staff allowed to freeze plans. */
  onFreeze?: () => void
  onUnfreeze?: () => void
}) {
  const { t } = useTranslation()
  const openFreeze = hasOpenFreeze(sub)
  return (
    <div className="rounded-lg bg-content2/50 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">{sub.planName}</p>
        <SubBadge status={sub.status} />
      </div>
      <SubscriptionDates sub={sub} />
      {sub.entriesRemaining != null && (
        <p className="mt-0.5 text-xs text-foreground/50">
          {t('members.subEntriesRemaining', { count: sub.entriesRemaining })}
        </p>
      )}
      {openFreeze && sub.frozenFrom && sub.frozenUntil && (
        <p className="mt-0.5 text-xs text-sky-700 dark:text-sky-300">
          {t('members.subFrozenRange', {
            from: formatDate(sub.frozenFrom),
            to: formatDate(sub.frozenUntil),
          })}
        </p>
      )}
      {onUnfreeze && openFreeze ? (
        <div className="mt-2 flex justify-end">
          <Button size="sm" variant="flat" onClick={onUnfreeze}>
            {t('members.unfreeze')}
          </Button>
        </div>
      ) : onFreeze && canFreeze(sub) ? (
        <div className="mt-2 flex justify-end">
          <Button size="sm" variant="flat" onClick={onFreeze}>
            {t('members.freeze')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export function PreviousSubscriptionRow({ sub }: { sub: ProfileSubscriptionData }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-between rounded-lg bg-content2/30 px-3 py-1.5">
      <div>
        <p className="text-sm text-foreground/70">
          {sub.planName}
          {sub.isAddon && (
            <span className="ml-2 rounded-full bg-secondary/10 px-2 py-0.5 text-xs font-medium text-secondary">
              {t('plans.addonChip')}
            </span>
          )}
        </p>
        <SubscriptionDates sub={sub} />
      </div>
      <SubBadge status={sub.status} />
    </div>
  )
}
